
// Standalone file type detection utility - safe for web workers

/**
 * Normalized file types
 */
export type FileType = 'jpeg' | 'png' | 'webp' | 'gif' | 'exr' | 'heic' | 'unknown';

/**
 * Normalizes MIME types to consistent format
 */
function normalizeFileType(mimeType: string): FileType {
  if (!mimeType) return 'unknown';
  
  switch (mimeType.toLowerCase()) {
    case 'image/jpeg':
    case 'image/jpg':
      return 'jpeg';
    case 'image/heic':
    case 'image/heif':
      return 'heic';
    case 'image/png':
      return 'png';
    case 'image/x-exr':
    case 'application/octet-stream': // EXR often detected as this
      return 'exr';
    case 'image/webp':
      return 'webp';
    case 'image/gif':
      return 'gif';
    default:
      return 'unknown';
  }
}

/**
 * Gets file type from extension as fallback
 */
function getTypeFromExtension(filename: string): FileType {
  const extension = filename.toLowerCase().split('.').pop();
  
  switch (extension) {
    case 'jpg':
    case 'jpeg':
      return 'jpeg';
    case 'heic':
    case 'heif':
      return 'heic';
    case 'png':
      return 'png';
    case 'exr':
      return 'exr';
    case 'webp':
      return 'webp';
    case 'gif':
      return 'gif';
    default:
      return 'unknown';
  }
}

/**
 * Main file type detection function
 * Uses MIME type first, falls back to extension
 * Safe for use in web workers (no external dependencies)
 */
export function getFileType(file: File): FileType {
  // First try MIME type
  let fileType: FileType = normalizeFileType(file.type);
  
  // If MIME type is unknown/ambiguous, fall back to extension
  if (fileType === 'unknown' || (fileType === 'exr' && file.type === 'application/octet-stream')) {
    // For ambiguous MIME types, double-check with extension
    const extensionType: FileType = getTypeFromExtension(file.name);
    if (extensionType !== 'unknown') {
      fileType = extensionType;
    }
  }
  
  return fileType;
}

/**
 * Check if a file type supports client-side blur detection
 */
export function supportsBlurDetection(fileType: FileType): boolean {
  const supportedFormats: FileType[] = ['jpeg', 'png', 'webp', 'gif'];
  return supportedFormats.includes(fileType);
}

/**
 * Get user-friendly label for file type
 */
export function getFileTypeLabel(fileType: FileType): string {
  switch (fileType) {
    case 'jpeg': return 'JPEG';
    case 'heic': return 'HEIC';
    case 'png': return 'PNG';
    case 'exr': return 'EXR';
    case 'webp': return 'WebP';
    case 'gif': return 'GIF';
    default: return 'Unknown';
  }
}

/**
 * Get all supported image formats
 */
export function getSupportedImageFormats(): FileType[] {
  return ['jpeg', 'png', 'webp', 'gif', 'heic', 'exr'];
}

/**
 * Check if a file format is supported for upload
 */
export function isImageFormatSupported(file: File): boolean {
  const fileType = getFileType(file);
  return getSupportedImageFormats().includes(fileType);
}